import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  customerValueApi,
  type BenchmarkSnapshot,
  type ExecutiveReport,
  type ExecutiveSummary,
  type LeakageRecoverySnapshot,
  type Milestone,
  type ProfitImpactSnapshot,
  type RecommendationImpactSnapshot,
  type RenewalCenterSnapshot,
  type RevenueImpactSnapshot,
  type SubscriptionROISnapshot,
  type SuccessScoreSnapshot,
  type TimelineEntry,
  type TimeSavedSnapshot,
  type ValueDashboardSnapshot,
} from '../../api/customer-value-cvd001-runtime';

type Tab = 'executive' | 'revenue' | 'profit' | 'leakage' | 'time' | 'recommendations' | 'roi' | 'timeline' | 'milestones' | 'renewal' | 'benchmark' | 'reports';

export function CustomerValuePage() {
  const [tab, setTab] = useState<Tab>('executive');
  const [dashboard, setDashboard] = useState<ValueDashboardSnapshot | null>(null);
  const [executive, setExecutive] = useState<ExecutiveSummary | null>(null);
  const [revenue, setRevenue] = useState<RevenueImpactSnapshot | null>(null);
  const [profit, setProfit] = useState<ProfitImpactSnapshot | null>(null);
  const [leakage, setLeakage] = useState<LeakageRecoverySnapshot | null>(null);
  const [timeSaved, setTimeSaved] = useState<TimeSavedSnapshot | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationImpactSnapshot | null>(null);
  const [successScore, setSuccessScore] = useState<SuccessScoreSnapshot | null>(null);
  const [roi, setRoi] = useState<SubscriptionROISnapshot | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [renewal, setRenewal] = useState<RenewalCenterSnapshot | null>(null);
  const [benchmark, setBenchmark] = useState<BenchmarkSnapshot | null>(null);
  const [reports, setReports] = useState<ExecutiveReport[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-emerald-200 bg-white dark:border-emerald-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-emerald-700 dark:text-emerald-300';
  const shell = 'bg-emerald-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, exec, rev, prof, leak, time, rec, score, roiSnap, tl, ms, ren, bench, rpts] = await Promise.all([
        customerValueApi.dashboard(), customerValueApi.executiveSummary(),
        customerValueApi.revenue(), customerValueApi.profit(), customerValueApi.leakage(),
        customerValueApi.timeSaved(), customerValueApi.recommendations(), customerValueApi.successScore(),
        customerValueApi.roi(), customerValueApi.timeline(), customerValueApi.milestones(),
        customerValueApi.renewal(), customerValueApi.benchmark(), customerValueApi.reports(),
      ]);
      setDashboard(dash); setExecutive(exec); setRevenue(rev); setProfit(prof); setLeakage(leak);
      setTimeSaved(time); setRecommendations(rec); setSuccessScore(score); setRoi(roiSnap);
      setTimeline(tl); setMilestones(ms); setRenewal(ren); setBenchmark(bench); setReports(rpts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Customer Value Dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'executive', label: 'Executive Summary' }, { id: 'revenue', label: 'Revenue' },
    { id: 'profit', label: 'Profit' }, { id: 'leakage', label: 'Recovery' },
    { id: 'time', label: 'Time Saved' }, { id: 'recommendations', label: 'Recommendations' },
    { id: 'roi', label: 'Subscription ROI' }, { id: 'timeline', label: 'Timeline' },
    { id: 'milestones', label: 'Milestones' }, { id: 'renewal', label: 'Renewal Center' },
    { id: 'benchmark', label: 'Benchmarks' }, { id: 'reports', label: 'Reports' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>CVD-000001 · Customer Value Dashboard</p>
            <h1 className="text-xl font-semibold">Customer ROI & Business Impact</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Business Value <strong className={accent}>{dashboard.business_value_inr}</strong></span>
                <span>ROI <strong className={accent}>{dashboard.subscription_roi}×</strong></span>
                <span>Grade <strong className={accent}>{dashboard.success_grade}</strong></span>
                <span>Time Saved <strong className={accent}>{dashboard.time_saved_hours}h</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/engineering-excellence" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Engineering Excellence</Link>
            <Link to="/platform/customer-value-cvd001" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading customer value dashboard…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-emerald-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'executive' && executive && successScore && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Business Health', value: executive.business_health },
            { label: 'Success Score', value: `${successScore.overall_score} (${successScore.overall_grade})` },
            { label: 'Subscription ROI', value: `${executive.subscription_roi}×` },
            { label: 'Business Value', value: executive.business_value_inr },
            { label: 'Platform Utilization', value: `${executive.platform_utilization_pct}%` },
            { label: 'Recommendation Adoption', value: `${executive.recommendation_adoption_pct}%` },
            { label: 'Trend', value: executive.improvement_trend },
            { label: 'Financial Score', value: successScore.financial_score },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-lg font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'revenue' && revenue && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Revenue Influenced', value: revenue.revenue_influenced_inr },
            { label: 'Revenue Improved', value: revenue.revenue_improved_inr },
            { label: 'Revenue Protected', value: revenue.revenue_protected_inr },
            { label: 'Revenue Recovered', value: revenue.revenue_recovered_inr },
            { label: 'Opportunity Captured', value: revenue.opportunity_captured_inr },
            { label: 'Loss Prevented', value: revenue.loss_prevented_inr },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'profit' && profit && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Additional Gross Profit', value: profit.additional_gross_profit_inr },
            { label: 'Additional Net Profit', value: profit.additional_net_profit_inr },
            { label: 'Margin Improvement', value: `${profit.margin_improvement_pct}%` },
            { label: 'Profit Protected', value: profit.profit_protected_inr },
            { label: 'Profit Recovered', value: profit.profit_recovered_inr },
            { label: 'Lifetime Profit', value: profit.lifetime_profit_inr },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'leakage' && leakage && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Marketplace Fees Recovered', value: leakage.marketplace_fees_recovered_inr },
            { label: 'Storage Fees Avoided', value: leakage.storage_fees_avoided_inr },
            { label: 'Ad Waste Eliminated', value: leakage.ad_waste_eliminated_inr },
            { label: 'Cash Recovered', value: leakage.cash_recovered_inr },
            { label: 'Pending Recovery', value: leakage.pending_recovery_inr },
            { label: 'Success Rate', value: `${leakage.success_rate_pct}%` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'time' && timeSaved && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Total Hours Saved', value: timeSaved.total_hours_saved },
            { label: 'Equivalent Days', value: timeSaved.equivalent_days },
            { label: 'Salary Equivalent', value: timeSaved.equivalent_salary_saved_inr },
            { label: 'Manual Hours', value: timeSaved.manual_hours_eliminated },
            { label: 'Automation Hours', value: timeSaved.automation_hours },
            { label: 'Reporting Hours', value: timeSaved.reporting_hours },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && recommendations && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Generated', value: recommendations.generated },
            { label: 'Accepted', value: recommendations.accepted },
            { label: 'Success Rate', value: `${recommendations.success_rate_pct}%` },
            { label: 'Revenue Generated', value: recommendations.revenue_generated_inr },
            { label: 'Profit Generated', value: recommendations.profit_generated_inr },
            { label: 'Rejected', value: recommendations.rejected },
            { label: 'Ignored', value: recommendations.ignored },
            { label: 'Confidence Gain', value: `${recommendations.confidence_improvement_pct}%` },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'roi' && roi && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className={`text-sm ${muted}`}>Subscription Paid</p>
              <p className="text-2xl font-semibold">{roi.subscription_paid_inr}</p>
            </div>
            <div>
              <p className={`text-sm ${muted}`}>Business Value Generated</p>
              <p className={`text-2xl font-semibold ${accent}`}>{roi.business_value_generated_inr}</p>
            </div>
            <div>
              <p className={`text-sm ${muted}`}>ROI Multiple</p>
              <p className="text-4xl font-bold text-emerald-600">{roi.roi_multiple}×</p>
            </div>
            <div>
              <p className={`text-sm ${muted}`}>Payback Period</p>
              <p className="text-2xl font-semibold">{roi.payback_period_days} days</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'timeline' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${muted}`}>
              <th className="p-3 text-left">Period</th><th className="p-3 text-left">Revenue</th>
              <th className="p-3 text-left">Profit</th><th className="p-3 text-left">ROI</th>
              <th className="p-3 text-left">Recommendations</th>
            </tr></thead>
            <tbody>
              {timeline.map((e) => (
                <tr key={e.period} className="border-b border-emerald-100 dark:border-emerald-900/30">
                  <td className="p-3">{e.period}</td><td className="p-3">{e.revenue_inr}</td>
                  <td className="p-3">{e.profit_inr}</td><td className="p-3">{e.roi}×</td>
                  <td className="p-3">{e.recommendations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'milestones' && (
        <div className="grid gap-3 sm:grid-cols-2">
          {milestones.map((m) => (
            <div key={m.id} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${accent}`}>{m.category}</p>
              <p className="font-semibold">{m.title}</p>
              <p className={`text-sm ${muted}`}>{m.description}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'renewal' && renewal && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <p className={`text-xs uppercase ${accent}`}>Renewal Justification</p>
          <p className="mt-2 text-lg">{renewal.executive_justification}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-4">
            {[
              { label: 'Value Since Joining', value: renewal.value_since_joining_inr },
              { label: 'ROI', value: `${renewal.roi_multiple}×` },
              { label: 'Profit', value: renewal.profit_inr },
              { label: 'Platform Usage', value: `${renewal.platform_usage_pct}%` },
            ].map((m) => (
              <div key={m.label}>
                <p className={`text-xs ${muted}`}>{m.label}</p>
                <p className="font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'benchmark' && benchmark && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { label: 'Customer Percentile', value: `${benchmark.customer_percentile}%` },
            { label: 'Category Percentile', value: `${benchmark.category_percentile}%` },
            { label: 'Marketplace Percentile', value: `${benchmark.marketplace_percentile}%` },
            { label: 'Gap to Top 10%', value: benchmark.top_10_pct_gap },
            { label: 'Growth vs Category', value: `${benchmark.growth_rate_vs_category}%` },
            { label: 'Trend', value: benchmark.improvement_trend },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'reports' && (
        <div className="grid gap-3">
          {reports.map((r) => (
            <div key={r.id} className={`flex items-center justify-between rounded-xl border p-4 ${card}`}>
              <div>
                <p className="font-semibold">{r.title}</p>
                <p className={`text-sm ${muted}`}>{r.period} · {r.type}</p>
              </div>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
